
import { supabase } from '@/integrations/supabase/client';
import { v4 as uuidv4 } from 'uuid';

// Unique session ID for tracking a user's session across logs
const SESSION_ID = uuidv4();

// Control global log level
const LOG_LEVEL = {
  NONE: 0,
  ERROR: 1,
  WARN: 2,
  INFO: 3,
  DEBUG: 4
};

// Set current log level - can adjust this based on environment
const CURRENT_LOG_LEVEL = process.env.NODE_ENV === 'production' 
  ? LOG_LEVEL.ERROR  // Only log errors in production
  : LOG_LEVEL.INFO;  // Log info and above in development

/**
 * Utility for logging mobile-specific metrics and diagnostics to help
 * debug authentication and session issues on mobile devices
 */
export const mobileDebugger = {
  /**
   * Log authentication-related events with mobile context
   */
  async logAuthEvent(
    eventType: 'init_start' | 'init_complete' | 'refresh_start' | 'refresh_complete' | 'session_error' | 'route_change',
    details: Record<string, any> = {}
  ) {
    try {
      // Skip logging if below current log level
      if (CURRENT_LOG_LEVEL < LOG_LEVEL.INFO) return;
      
      // Implement log rate limiting
      if (!this.shouldLog(eventType)) return;
      
      // Get connection info if available (mobile browsers may have this)
      const connectionInfo = getConnectionInfo();
      
      // Get device info
      const deviceInfo = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        vendor: navigator.vendor,
        screen: {
          width: window.screen.width,
          height: window.screen.height,
          orientation: window.screen.orientation?.type || 'unknown',
          pixelRatio: window.devicePixelRatio,
        },
        viewport: {
          width: window.innerWidth, 
          height: window.innerHeight,
        },
        connection: connectionInfo,
        memory: (navigator as any).deviceMemory,
        prefersDarkMode: window.matchMedia?.('(prefers-color-scheme: dark)')?.matches,
        isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
      };

      // Get current session (if it exists, don't fail if not)
      let sessionInfo = {};
      try {
        const { data } = await supabase.auth.getSession();
        sessionInfo = {
          hasSession: !!data.session,
          expiresAt: data.session?.expires_at
        };
      } catch (err) {
        sessionInfo = { sessionError: String(err) };
      }

      // Useful timestamps
      const timestamps = {
        clientTime: new Date().toISOString(),
        clientTimestamp: Date.now(),
        utcOffsetMinutes: new Date().getTimezoneOffset(),
      };
      
      // Collect performance metrics if available
      const performanceMetrics = getPerformanceMetrics();
      
      // Prepare the log data
      const logData = {
        mobile_session_id: SESSION_ID,
        event_type: eventType,
        url: window.location.href,
        path: window.location.pathname,
        ...timestamps,
        ...sessionInfo,
        ...deviceInfo,
        ...performanceMetrics,
        ...details
      };

      // First, always log to console for immediate visibility during testing
      console.log(`📱 Mobile debug [${eventType}]`, logData);

      // Only log to database if it's an important event or error
      if (['session_error', 'init_complete'].includes(eventType)) {
        await supabase
          .from('system_health_metrics')
          .insert({
            metric_name: `mobile_auth_${eventType}`,
            value: logData
          });
      }

    } catch (error) {
      // Never fail the app due to logging
      console.error('Error logging mobile auth event:', error);
    }
  },

  /**
   * Log client errors with mobile context
   */
  async logError(error: any, context: string = 'unknown', additionalData: Record<string, any> = {}) {
    try {
      // Always log errors regardless of log level
      console.error(`📱 Mobile Error [${context}]:`, error);
      
      await supabase
        .from('system_health_metrics')
        .insert({
          metric_name: 'mobile_error',
          value: {
            mobile_session_id: SESSION_ID,
            context,
            error_message: error?.message || String(error),
            error_stack: error?.stack,
            error_name: error?.name,
            url: window.location.href,
            path: window.location.pathname,
            timestamp: Date.now(),
            userAgent: navigator.userAgent,
            ...additionalData
          }
        });
    } catch (logError) {
      console.error('Failed to log error:', logError);
    }
  },
  
  // Rate limiting for logs
  logHistory: new Map<string, number>(),
  
  // Check if we should log this event based on rate limiting
  shouldLog(eventType: string): boolean {
    const now = Date.now();
    const key = eventType;
    const lastLog = this.logHistory.get(key) || 0;
    
    // Define minimum time between logs of the same type (in milliseconds)
    const minInterval = {
      'init_start': 5000,
      'init_complete': 5000,
      'refresh_start': 10000,
      'refresh_complete': 10000,
      'session_error': 1000, // Log errors more frequently
      'route_change': 2000,
      'default': 5000
    };
    
    const interval = minInterval[eventType as keyof typeof minInterval] || minInterval.default;
    
    if (now - lastLog < interval) {
      return false; // Too soon to log again
    }
    
    // Update last log time
    this.logHistory.set(key, now);
    return true;
  }
};

/**
 * Get connection information if available in the browser
 */
function getConnectionInfo() {
  // Get network connection info if available (mobile browsers)
  const nav = navigator as any;
  if (nav.connection) {
    return {
      effectiveType: nav.connection.effectiveType,
      downlink: nav.connection.downlink,
      rtt: nav.connection.rtt,
      saveData: nav.connection.saveData,
    };
  }
  return null;
}

/**
 * Get performance metrics if available
 */
function getPerformanceMetrics() {
  if (!window.performance) {
    return { performance_api_supported: false };
  }

  try {
    const navigation = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const memory = (performance as any).memory;
    
    return {
      performance_api_supported: true,
      page_load_time: navigation ? navigation.loadEventEnd - navigation.startTime : null,
      dom_complete: navigation ? navigation.domComplete : null,
      used_js_heap_size: memory ? memory.usedJSHeapSize : null,
      total_js_heap_size: memory ? memory.totalJSHeapSize : null,
    };
  } catch (err) {
    return { performance_api_supported: false, performance_error: String(err) };
  }
}
